'use client'

import React from 'react'
import Link from 'next/link'
import { Title } from '@/ui/components/Title'
import { Button } from '@/ui/atoms/Button'
import { LockSvg } from '@/ui/components/KnowledgeHubV2/icons'
import { useSupabaseAuth } from '@/context/supabaseContext'
import { trackEvent } from '@/lib/analytics/posthog'
import { useDownloadGate } from '@/lib/downloads/useDownloadGate'

// Declared locally rather than imported from payload-types: this block was added after
// the static cutover, and `payload generate:types` has not been re-run against it.
export type ToolEmbedProps = {
  blockType?: 'toolEmbed'
  title?: string | null
  intro?: string | null
  url?: string | null
  attribution?: string | null
  height?: number | null
  disclaimer?: string | null
  /** Gate the full tool (full-height frame, standalone link) behind a free account. */
  requiresAuth?: boolean | null
  /** Row label in the access panel, e.g. the tool's name. */
  accessLabel?: string | null
  /** Small print under the label, e.g. "10 calculators · runs in your browser". */
  accessMetainfo?: string | null
  /** Where to send the reader back to after signing in. */
  returnPath?: string | null
  id?: string | null
}

/** Signed-out frame height: enough to show the tool is real, short enough to read as a preview. */
const PREVIEW_HEIGHT = 520

/**
 * Inline third-party tool with an access gate.
 *
 * Signed out, the frame is a PREVIEW and says so three times: a notice bar above it, a
 * fade-out with a sign-in button over its lower edge, and the header link. The reader can
 * try the top of the tool, but it is cut short and every surface points at the free
 * account. Signed in, the full-height frame and the standalone link are shown plain.
 *
 * The access row also lives in KnowledgeDownloads alongside the file downloads, so a reader
 * sees everything the entry offers in one list. This block only renders the frame.
 *
 * Note this is a conversion prompt, not access control: the tool's URL is in the page
 * source either way. Anything that must actually be restricted cannot live in an iframe.
 *
 * Sandboxed: scripts and same-origin are granted so the app runs, but top-navigation,
 * downloads, popups and form submission are withheld, so an embedded page cannot
 * redirect the reader away from EA or push a file at them.
 */
export const ToolEmbedBlock: React.FC<ToolEmbedProps> = ({
  title,
  intro,
  url,
  attribution,
  height,
  disclaimer,
  requiresAuth,
  accessLabel,
  returnPath,
}) => {
  // Client component: the member session only exists in the browser. Before 2026-09-17
  // this hardcoded a signed-out state, so members still got the cut-short preview.
  const { user } = useSupabaseAuth()
  const { notifyToolOpen } = useDownloadGate()

  if (!url) return null

  let host = ''
  try {
    host = new URL(url).host
  } catch {
    return null
  }

  const locked = Boolean(requiresAuth) && !user
  const signInHref = `/login?next=${encodeURIComponent(returnPath || '/')}`
  const toolName = accessLabel || title || 'this tool'
  const fullHeight = height ?? 820
  const frameHeight = locked ? Math.min(PREVIEW_HEIGHT, fullHeight) : fullHeight
  // Same event the file gates send, so every "sign in to get this" tap lands in one report.
  const trackGate = () =>
    trackEvent('product_sheet_login_gate_click', {
      file_title: toolName,
      page_path: returnPath || '/',
      surface: 'tool_embed',
    })

  return (
    <div>
      <Title hierarchy="h2" className="mb-8">
        {title}
      </Title>

      {intro && <p className="text-body-M-regular text-text-base mb-6">{intro}</p>}

      <div className="rounded-md border border-border-default overflow-hidden bg-bg-subtle">
        <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-border-default">
          <span className="text-body-XS-regular text-text-faded truncate">
            {attribution ? `${attribution} · ` : ''}
            {host}
          </span>
          {locked ? (
            <Link
              href={signInHref}
              onClick={trackGate}
              className="flex items-center gap-1.5 text-body-XS-semibold text-text-base underline whitespace-nowrap"
            >
              <LockSvg className="w-3.5 h-3.5" />
              Sign in for full access
            </Link>
          ) : (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              onClick={() => user && notifyToolOpen(toolName, url)}
              className="text-body-XS-semibold text-text-base underline whitespace-nowrap"
            >
              Open in a new tab ↗
            </a>
          )}
        </div>

        {locked && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-b border-border-default bg-bg-base">
            <div className="flex items-start gap-2 min-w-0">
              <LockSvg className="w-4 h-4 mt-0.5 shrink-0 text-text-base" />
              <div className="min-w-0">
                <p className="text-body-S-semibold text-text-base">
                  Preview only. A free account unlocks the full tool.
                </p>
                <p className="text-body-XS-regular text-text-subtle">
                  Sign in to use every calculator in {toolName} at full size and open it in its own tab.
                </p>
              </div>
            </div>
            <Button variant="primary" size="sm" asChild>
              <Link href={signInHref} onClick={trackGate} className="whitespace-nowrap">
                <LockSvg className="w-4 h-4" />
                Sign in for full access
              </Link>
            </Button>
          </div>
        )}

        <div className="relative">
          <iframe
            src={url}
            title={locked ? `${title ?? 'Embedded tool'} (preview)` : (title ?? 'Embedded tool')}
            loading="lazy"
            referrerPolicy="no-referrer"
            sandbox="allow-scripts allow-same-origin"
            className="block w-full bg-white"
            style={{ height: `${frameHeight}px`, border: 0 }}
          />
          {locked && (
            <div className="absolute inset-x-0 bottom-0 flex flex-col items-center justify-end gap-2 px-4 pb-5 pt-24 text-center bg-gradient-to-t from-white via-white/95 to-transparent">
              <p className="text-body-S-semibold text-text-base">
                This preview is cut short. Sign in to unlock the full tool.
              </p>
              <p className="text-body-XS-regular text-text-subtle">
                Free account · takes a minute · brings you straight back here
              </p>
              <Button variant="primary" size="sm" asChild>
                <Link href={signInHref} onClick={trackGate} className="whitespace-nowrap">
                  <LockSvg className="w-4 h-4" />
                  Sign in for full access
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>

      {disclaimer && <p className="mt-3 text-body-XS-regular text-text-faded">{disclaimer}</p>}
    </div>
  )
}
